import { useState, useEffect } from 'react'
import { Head, usePage } from '@inertiajs/react'
import { Github } from 'lucide-react'
import type { SharedPageProps } from '@/types/shared'
import CandidateHowItWorks from '@/components/Shared/CandidateHowItWorks'
import IndividualWhyHyrin from '@/components/Shared/IndividualWhyHyrin'
import { HyrinWordmark } from '@/components/Shared/HyrinWordmark'

type ActiveModal = 'howItWorks' | 'whyHyrin' | null

interface SignInProps {
  providerLabel: string
  csrfToken: string
}

const ROTATING_MESSAGES: { headline: string; secondary?: string }[] = [
  { headline: 'Get hired for it.', secondary: 'Join us on the other side!' },
  {
    headline: 'Hyrin shows where you fit.',
    secondary: "Don't wait until you need a job!",
  },
  {
    headline: 'Keep getting real offers.',
    secondary: 'Let the companies chase you!',
  },
  { 
    headline: 'Stop applying as keywords.',
    secondary: 'Get out of the rat race!',
  },
  { 
    headline: 'Keep your Hyrin profile ready', 
    secondary: 'Your next opportunity should not start from panic!' 
  },
]

export default function SignIn({ providerLabel, csrfToken }: SignInProps) {
  const { flash } = usePage<SharedPageProps>().props
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)
  const [messageIndex, setMessageIndex] = useState(0)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % ROTATING_MESSAGES.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <Head title="Sign in — Hyrin" />

      <div className="flex min-h-screen border-b border-border bg-muted">
        <div className="flex w-full flex-col">
          <header className="flex h-16 items-center justify-between border-b border-border px-9">
            <a href="/" className="flex items-center gap-2">
              <HyrinWordmark />
              <span className="ml-3 border-l border-border pl-3 font-mono text-[12px] text-muted-foreground">
                Your agent for your AI jobs
              </span>
            </a>
            <nav className="flex items-center gap-4">
              <button
                onClick={() => setActiveModal('howItWorks')}
                className="font-sans text-[13px] text-muted-foreground transition-colors hover:text-foreground"
              >
                How it works?
              </button>
              <button
                onClick={() => setActiveModal('whyHyrin')}
                className="font-sans text-[13px] text-muted-foreground transition-colors hover:text-foreground"
              >
                Why Hyrin?
              </button>
              <span className="font-sans text-[13px] text-muted-foreground">
                For individuals
              </span>
              <a
                href="/for-organizations"
                className="flex h-[34px] items-center rounded-[8px] bg-foreground px-4 font-sans text-[13px] font-semibold text-background"
              >
                For organizations
              </a>
            </nav>
          </header>

          <div className="grid flex-1 grid-cols-[1.05fr_0.95fr]">
            {/* Left: form */}
            <div className="flex flex-col justify-center px-14 py-16">
              <p className="mb-[22px] font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
                Sign in
              </p>

              <h1 className="mb-[18px] font-heading text-5xl font-bold leading-[1.04] tracking-[-0.03em] text-foreground">
                Show us how you build.
              </h1>

              <p className="mb-[34px] max-w-[380px] font-sans text-base leading-[1.6] text-muted-foreground">
                Code is cheap, show us how you talk to LLM. Get your MCP token and complete your
                profile.
              </p>

              {flash?.alert && (
                <div className="mb-4 max-w-[380px] rounded-[8px] border border-destructive/40 bg-destructive/10 px-4 py-3 font-sans text-[13px] text-destructive">
                  {flash.alert}
                </div>
              )}
              {flash?.notice && !flash?.alert && (
                <div className="mb-4 max-w-[380px] rounded-[8px] border border-border bg-primary/10 px-4 py-3 font-sans text-[13px] text-primary">
                  {flash.notice}
                </div>
              )}

              <form method="post" action="/auth/github" className="w-full max-w-[380px]">
                <input type="hidden" name="authenticity_token" value={csrfToken} />
                <button
                  type="submit"
                  className="flex h-[54px] w-full items-center justify-center gap-3 rounded-[11px] bg-foreground font-sans text-base font-semibold text-background shadow-sm transition-opacity hover:opacity-90"
                >
                  <Github className="size-6" aria-hidden="true" />
                  Continue with {providerLabel}
                </button>
              </form>

              <p className="mt-4 font-mono text-[12px] leading-[1.5] text-muted-foreground">
                We only read your public profile to confirm identity.
              </p>
            </div>

            {/* Right: panel */}
            <div className="flex flex-col items-center justify-center border-l border-border bg-background px-11 py-9 text-center">
              <img
                src="/landing.png"
                alt="Hyrin — get hired for how you build"
                className="mb-1 w-[400px] drop-shadow-[0_26px_32px_rgba(20,40,32,0.2)]"
              />
              <div className="min-h-[9rem]">
                <h2 className="mb-[10px] mt-0 font-heading text-5xl font-bold leading-[1.04] tracking-[-0.03em] text-foreground">
                  {ROTATING_MESSAGES[messageIndex]?.headline}
                </h2>
                <p className="font-sans text-base leading-[1.5] text-primary">
                  {ROTATING_MESSAGES[messageIndex]?.secondary}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CandidateHowItWorks
        isOpen={activeModal === 'howItWorks'}
        onClose={() => setActiveModal(null)}
      />
      <IndividualWhyHyrin
        isOpen={activeModal === 'whyHyrin'}
        onClose={() => setActiveModal(null)}
      />
    </>
  )
}
